import React, { type ComponentProps } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import type { AddressType } from '@sg/garnish';
import { theme, useResponsive, VStack } from '@sg/garnish';
import type { CourierDetails } from '@sg/graphql-schema';
import type { Ledger } from '@sg/graphql-schema';
import { type DeliveryPreferenceType, FlattenedOrderStatuses } from '@sg/graphql-schema';

import { ignoreTimezone } from '@order/utils';

import { OrderToHealthKit } from '../../../../components';
import { type PartialLineItem } from '../../../../graphql';
import { type ReorderOrder } from '../../../../hooks';
import { useHasEarnedLoyaltyPoints, type useOrderStatusRequest } from '../../hooks';
import { OrderStatusAddress } from '../OrderStatusAddress';
import { OrderStatusCourierInfo } from '../OrderStatusCourierInfo';
import { OrderStatusLedger } from '../OrderStatusLedger';
import { OrderStatusLoyaltyPointsBanner } from '../OrderStatusLoyaltyPointsBanner';
import { OrderStatusPayments } from '../OrderStatusPayments';

export const OrderStatusDetails = (props: OrderStatusDetailsProps) => {
  const {
    orderId,
    orderType,
    orderStatus,
    isOrderCancelled,
    courierName,
    courierPhone,
    payments,
    isOrderHealthKitEnabled,
    orderWantedTime,
    orderToHealthKitLineItems,
    hasDeliveryAddress,
  } = props;

  const hasEarnedLoyaltyPoints = useHasEarnedLoyaltyPoints(orderId, isOrderCancelled);

  const shouldShowCourierInfo =
    orderType === 'delivery' &&
    orderStatus === FlattenedOrderStatuses.Delivering &&
    courierName &&
    courierPhone;

  const shouldShowAddress = orderType !== 'delivery' || hasDeliveryAddress;

  const shouldShowPayments = payments.length > 0;

  const shouldRenderOrderToHealthKit =
    isOrderHealthKitEnabled &&
    orderToHealthKitLineItems?.length !== undefined &&
    orderWantedTime !== undefined &&
    Platform.OS === 'ios';

  return (
    <VStack gap={0} divider={<Divider />}>
      {hasEarnedLoyaltyPoints ? <OrderStatusLoyaltyPointsBanner /> : null}

      {shouldShowCourierInfo ? <OrderStatusCourierInfo {...props} /> : null}

      {shouldShowAddress ? <OrderStatusAddress {...props} /> : null}

      <OrderStatusLedger {...props} />

      {shouldShowPayments ? <OrderStatusPayments payments={props.payments} /> : null}

      {shouldRenderOrderToHealthKit ? (
        <OrderToHealthKit
          lineItems={orderToHealthKitLineItems}
          orderWantedTime={ignoreTimezone(orderWantedTime)}
        />
      ) : null}
    </VStack>
  );
};

const Divider = () => {
  const { match } = useResponsive();

  return <View style={[styles.divider, match([styles.dividerXS, styles.dividerSM])]} />;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  divider: {
    height: 8,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
    marginHorizontal: -theme.spacing['10'],
  },
  dividerXS: {
    marginVertical: theme.spacing['6'],
  },
  dividerSM: {
    marginVertical: theme.spacing['10'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderStatusDetailsProps = Readonly<{
  order: ReorderOrder;
  orderId: string;
  orderType: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  payments: ReturnType<typeof useOrderStatusRequest>['payments'];
  isOrderCancelled: boolean;
  hasDeliveryAddress: boolean;
  deliveryAddressName?: string;
  deliveryAddressStreet?: string;
  deliveryAddressSecondaryStreet?: string;
  deliveryAddressCity?: string;
  deliveryAddressState?: string;
  deliveryAddressZipCode?: string;
  deliveryAddressNotes?: string;
  deliveryPreference?: DeliveryPreferenceType;
  locationName?: string;
  locationAddress?: string;
  locationCity?: string;
  locationState?: string;
  locationZipCode?: string;
  lineItems: readonly PartialLineItem[];
  dropoffLocation?: string;
  ledger: Partial<Ledger>;
  courierName?: CourierDetails['name'];
  courierPhone?: CourierDetails['phone'];
  isOrderHealthKitEnabled: boolean;
  orderWantedTime?: string;
  orderToHealthKitLineItems: ComponentProps<typeof OrderToHealthKit>['lineItems'];
}>;
